import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import {
    ResponsiveContainer,
    LineChart,
    Line,
    XAxis,
    YAxis,
    Tooltip,
    CartesianGrid,
    ReferenceArea,
    ReferenceLine,
} from "recharts";

// Muted tonal colors at ~60% opacity
const COLORS = {
    price: "rgba(160, 210, 180, 0.65)",       // muted green
    assetMgr: "rgba(120, 190, 150, 0.60)",    // light green
    leveraged: "rgba(130, 170, 210, 0.60)",   // blue
    retail: "rgba(160, 190, 220, 0.60)",      // light blue
};
const COLORS_SOLID = {
    price: "#a0d2b4",
    assetMgr: "#78be96",
    leveraged: "#82aad2",
    retail: "#a0bedc",
};

const RUSSELL_EVENTS = [
    { start: "2018-10-01", end: "2018-12-31", label: "Q4 selloff", sub: "OCT–DEC" },
    { start: "2019-09-01", end: "2019-10-31", label: "Repo stress", sub: "SEP–OCT" },
    { start: "2020-02-01", end: "2020-04-30", label: "COVID", sub: "FEB–APR" },
    { start: "2020-11-01", end: "2021-03-31", label: "Vaccine / Reflation", sub: "NOV–MAR" },
    { start: "2022-01-01", end: "2022-06-30", label: "Inflation / Hikes", sub: "JAN–JUN" },
    { start: "2023-03-01", end: "2023-05-31", label: "Regional bank stress", sub: "MAR–MAY" },
    { start: "2026-01-01", end: "2026-03-31", label: "Small-cap lead", sub: "JAN–MAR" },
];

const SP500_EVENTS = [
    { start: "2018-10-01", end: "2018-12-31", label: "Q4 selloff", sub: "OCT–DEC" },
    { start: "2019-09-01", end: "2019-10-31", label: "Repo stress", sub: "SEP–OCT" },
    { start: "2020-02-01", end: "2020-04-30", label: "COVID", sub: "FEB–APR" },
    { start: "2020-11-01", end: "2021-03-31", label: "Vaccine / Reflation", sub: "NOV–MAR" },
    { start: "2022-01-01", end: "2022-06-30", label: "Inflation / Hikes", sub: "JAN–JUN" },
    { start: "2025-04-01", end: "2025-04-30", label: "Tariff Surprise", sub: "APR" },
    { start: "2026-03-01", end: "2026-03-31", label: "Iran oil shock", sub: "MAR" },
];

const MAJOR_EVENTS_TEXT = `2018-10 to 2018-12 | Q4 selloff
Pivot window from growth-to-defensive, with the S&P 500 down about 13–14% in the quarter and volatility spiking.

2019-09 to 2019-10 | Repo Stress / QE-front end
Overnight repo and front-end funding rates spiked. QE at the front end to stabilize money-market rates and restore liquidity.

2020-02 to 2020-04 | COVID crash / Policy-driven recovery
Fastest drawdown and equally rapid policy-driven recovery; the S&P 500 fell roughly a third peak-to-trough before retracing into year-end.

2020-11 to 2021-03 | Vaccine / Reflation
Vaccine + fiscal impulse + steepening curve; large-cap index made new highs while cyclicals and small caps outperformed.

2022-01 to 2022-06 | Inflation / Hikes
Start of the hiking cycle; inflation and policy dominate large-cap factor behavior.

2023-03 to 2023-05 | Regional bank stress
Several U.S. mid-sized banks collapsed. Triggered rapid deposit flight, emergency policy support, and a sharp selloff in bank equities.

2025-04 | Tariff Surprise
The tariff-driven risk-off event. S&P 500 dropped almost 5% on April 3 alone and volatility spiking as investors priced in weaker growth and higher inflation risk.

2026-01 to 2026-03 | Small-cap leadership
Small-cap outperformance.

2026-03 | Iran oil shock / energy-led risk-off
The Iran conflict and effective closure of the Strait of Hormuz drove a sharp spike in oil prices, renewed inflationary pressures.`;

export default function PositioningOverlay({ api, section, expanded }) {
    const [weeklyData, setWeeklyData] = useState(null);
    const [metric1, setMetric1] = useState(null);
    const [metric2, setMetric2] = useState(null);
    const [loading, setLoading] = useState(true);

    const isRussell = section === "russell";
    const m1Id = isRussell ? "0101" : "0103";
    const m2Id = isRussell ? "0102" : "0104";
    const priceKey = isRussell ? "rty" : "spx";
    const events = isRussell ? RUSSELL_EVENTS : SP500_EVENTS;

    const title = isRussell
        ? "Russell 2000 : Positioning + Price Overlay"
        : "SP500 : Positioning + Price Overlay";
    const description = isRussell
        ? "This chart shows how shifts in institutional positioning (asset managers\u2019 and leveraged funds\u2019) line up with major moves in small-cap equities via IWM. It portrays whether extreme net long or short positioning by either group tends to coincide with local peaks, troughs, or regime changes in the small-cap equity index."
        : "This chart shows how shifts in institutional and individual investors\u2019 positioning line up with major moves in S&P 500 equities via SPY. It illustrates whether extreme net long or short exposure by either group tends to coincide with local peaks, troughs, or broader regime changes in the large-cap equity index.";

    const line2Label = isRussell ? "Asset Mgr %OI" : "Institutional (AM+LF) %OI";
    const line3Label = isRussell ? "Leveraged Fund %OI" : "Individual Investor (Retail):(NonRept) %OI";

    useEffect(() => {
        let alive = true;
        Promise.all([
            axios.get(`${api}/indexes/weekly`),
            axios.get(`${api}/metrics/${m1Id}`),
            axios.get(`${api}/metrics/${m2Id}`),
        ])
            .then(([wRes, m1Res, m2Res]) => {
                if (!alive) return;
                setWeeklyData(wRes.data.series);
                setMetric1(m1Res.data);
                setMetric2(m2Res.data);
                setLoading(false);
            })
            .catch((err) => { 
                if (alive) {
                    console.error("PositioningOverlay load error:", err?.message);
                    setLoading(false); 
                }
            });
        return () => { alive = false; };
    }, [api, m1Id, m2Id]);

    const chartData = useMemo(() => {
        if (!weeklyData || !metric1) return [];
        const priceMap = {};
        weeklyData.forEach((d) => { priceMap[d.date] = d[priceKey]; });
        const m1Map = {};
        metric1.series.forEach((d) => { m1Map[d.date] = d.net_pct_oi; });
        const m2Map = {};
        if (metric2) metric2.series.forEach((d) => { m2Map[d.date] = d.net_pct_oi; });

        const allDates = new Set([...Object.keys(priceMap), ...Object.keys(m1Map)]);
        return Array.from(allDates)
            .sort()
            .map((date) => ({
                date,
                t: new Date(date).getTime(),
                price: priceMap[date] ?? null,
                m1: m1Map[date] ?? null,
                m2: m2Map[date] ?? null,
            }))
            .filter((d) => d.price != null || d.m1 != null);
    }, [weeklyData, metric1, metric2, priceKey]);

    const chartHeight = expanded ? 500 : 260;

    if (loading) {
        return (
            <div className="pord-body py-12 text-center" style={{ fontSize: 13 }}>
                loading positioning overlay\u2026
            </div>
        );
    }

    return (
        <div data-testid={`overlay-${section}`}>
            <h3 className="pord-heading text-xl sm:text-2xl text-white mb-2">{title}</h3>
            {expanded && (
                <p className="pord-body mb-8 max-w-3xl" style={{ fontSize: 15, lineHeight: 1.85, color: "rgba(200, 205, 195, 0.85)" }}>{description}</p>
            )}
            {expanded && (
                <p className="pord-label mb-2 text-right" style={{ fontSize: 10, color: "var(--pord-sage)" }}>
                    Scroll down for Major Events description
                </p>
            )}
            <div style={{ width: "100%", height: chartHeight }}>
                {chartData.length === 0 ? (
                    <div className="pord-body h-full flex items-center justify-center">loading\u2026</div>
                ) : (
                    <ResponsiveContainer width="100%" height="100%">
                        <LineChart data={chartData} margin={{ top: 12, right: 60, bottom: 0, left: 0 }}>
                            <CartesianGrid stroke="rgba(255,255,255,0.06)" vertical={false} />
                            <XAxis
                                dataKey="t"
                                type="number"
                                scale="time"
                                domain={["dataMin", "dataMax"]}
                                tickFormatter={(t) =>
                                    new Date(t).toLocaleDateString("en-US", { year: "numeric", month: "short" })
                                }
                                tickLine={false}
                                axisLine={{ stroke: "rgba(255,255,255,0.08)" }}
                                minTickGap={60}
                            />
                            <YAxis
                                yAxisId="pct"
                                tickFormatter={(v) => `${v.toFixed(0)}%`}
                                tickLine={false}
                                axisLine={false}
                                width={50}
                            />
                            <YAxis
                                yAxisId="price"
                                orientation="right"
                                tickLine={false}
                                axisLine={false}
                                width={55}
                                tickFormatter={(v) => v.toFixed(0)}
                            />
                            <Tooltip content={<OverlayTooltip isRussell={isRussell} />} />

                            {/* 0% reference line separating positive/negative */}
                            <ReferenceLine
                                y={0}
                                yAxisId="pct"
                                stroke="rgba(255,255,255,0.25)"
                                strokeDasharray="3 3"
                                label={expanded ? { value: "0%", position: "insideLeft", fill: "rgba(255,255,255,0.4)", fontSize: 10 } : undefined}
                            />

                            {/* Shaded event periods with month labels */}
                            {events.map((e, i) => (
                                <ReferenceArea
                                    key={i}
                                    x1={new Date(e.start).getTime()}
                                    x2={new Date(e.end).getTime()}
                                    fill="rgba(184, 212, 208, 0.13)"
                                    stroke="rgba(184, 212, 208, 0.38)"
                                    strokeDasharray="2 3"
                                    yAxisId="pct"
                                    ifOverflow="extendDomain"
                                    label={(props) => renderShadeLabel(props, e.label, e.sub)}
                                />
                            ))}

                            <Line yAxisId="price" type="monotone" dataKey="price" stroke={COLORS.price} strokeWidth={1.5} dot={false} name={isRussell ? "RTY" : "SPX"} connectNulls />
                            <Line yAxisId="pct" type="monotone" dataKey="m1" stroke={COLORS.assetMgr} strokeWidth={1.2} dot={false} name={line2Label} connectNulls />
                            <Line yAxisId="pct" type="monotone" dataKey="m2" stroke={isRussell ? COLORS.leveraged : COLORS.retail} strokeWidth={1.2} dot={false} name={line3Label} connectNulls />
                        </LineChart>
                    </ResponsiveContainer>
                )}
            </div>
            {expanded && (
                <div className="mt-6">
                    <div className="flex flex-wrap gap-5 text-xs font-light mb-4">
                        <span style={{ color: COLORS_SOLID.price }}><span style={{ display: "inline-block", width: 14, height: 2, background: COLORS_SOLID.price, marginRight: 6, verticalAlign: "middle" }} />{isRussell ? "RTY Price" : "SPX Price"}</span>
                        <span style={{ color: COLORS_SOLID.assetMgr }}><span style={{ display: "inline-block", width: 14, height: 2, background: COLORS_SOLID.assetMgr, marginRight: 6, verticalAlign: "middle" }} />{line2Label}</span>
                        <span style={{ color: isRussell ? COLORS_SOLID.leveraged : COLORS_SOLID.retail }}><span style={{ display: "inline-block", width: 14, height: 2, background: isRussell ? COLORS_SOLID.leveraged : COLORS_SOLID.retail, marginRight: 6, verticalAlign: "middle" }} />{line3Label}</span>
                    </div>
                    <div className="pord-divider mb-6" />
                    <div className="pord-label mb-4">Major Events</div>
                    <pre
                        className="whitespace-pre-wrap"
                        style={{ color: "rgba(200, 205, 195, 0.82)", lineHeight: 2, fontFamily: "'DM Sans', sans-serif", fontWeight: 300, fontSize: 14.5 }}
                    >
                        {MAJOR_EVENTS_TEXT}
                    </pre>
                </div>
            )}
        </div>
    );
}

function renderShadeLabel(props, label, sub) {
    const { viewBox } = props;
    if (!viewBox) return null;
    const { x, y, width } = viewBox;
    const cx = x + width / 2;
    return (
        <g>
            <text
                x={cx}
                y={y + 14}
                fill="rgba(184, 212, 208, 0.90)"
                fontSize={12}
                fontWeight={400}
                textAnchor="middle"
                style={{ letterSpacing: "0.04em" }}
            >
                {label}
            </text>
            <text
                x={cx}
                y={y + 28}
                fill="rgba(184, 212, 208, 0.60)"
                fontSize={10}
                textAnchor="middle"
                style={{ letterSpacing: "0.12em", textTransform: "uppercase" }}
            >
                {sub}
            </text>
        </g>
    );
}

function OverlayTooltip({ active, payload, isRussell }) {
    if (!active || !payload?.length) return null;
    const p = payload[0]?.payload;
    if (!p) return null;
    return (
        <div className="pord-surface px-4 py-3 text-xs" style={{ background: "rgba(26,29,18,0.92)" }}>
            <div className="pord-label mb-1">{p.date}</div>
            {p.price != null && <div style={{ color: COLORS_SOLID.price }}>{isRussell ? "RTY" : "SPX"}: {p.price.toFixed(2)}</div>}
            {p.m1 != null && <div style={{ color: COLORS_SOLID.assetMgr }}>{isRussell ? "AM %OI" : "Inst (AM+LF) %OI"}: {p.m1.toFixed(2)}%</div>}
            {p.m2 != null && <div style={{ color: isRussell ? COLORS_SOLID.leveraged : COLORS_SOLID.retail }}>{isRussell ? "LF %OI" : "Indiv Investor %OI"}: {p.m2.toFixed(2)}%</div>}
        </div>
    );
}
